/**
 * Language Selector Component
 * Dropdown for switching article language in multi-language brands
 */

import { useState, useRef, useEffect } from 'react';
import { Globe, Check, ChevronDown } from 'lucide-react';
import { getLocaleInfo } from '@/types';

interface LanguageSelectorProps {
  currentLocale: string;
  availableLocales: string[];
  onLocaleChange: (locale: string) => void;
}

export function LanguageSelector({
  currentLocale,
  availableLocales,
  onLocaleChange,
}: LanguageSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Don't render if only one locale available
  if (availableLocales.length <= 1) {
    return null;
  }

  const currentLocaleInfo = getLocaleInfo(currentLocale);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground border border-input rounded-lg hover:bg-muted/50 transition-colors"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <Globe className="h-4 w-4" />
        <span>{currentLocaleInfo?.native_name || currentLocale}</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 w-48 bg-background border border-input rounded-lg shadow-lg z-50 py-1 max-h-64 overflow-y-auto">
          {availableLocales.map((locale) => {
            const localeInfo = getLocaleInfo(locale);
            const isSelected = locale === currentLocale;
            
            return (
              <button
                key={locale}
                type="button"
                onClick={() => {
                  onLocaleChange(locale);
                  setIsOpen(false);
                }}
                className={`
                  w-full flex items-center justify-between px-3 py-2 text-sm text-left transition-colors
                  ${isSelected ? 'bg-primary/5 text-primary' : 'hover:bg-muted/50'}
                `}
                role="option"
                aria-selected={isSelected}
              >
                <span className="flex items-center gap-2">
                  <span>{localeInfo?.native_name || locale}</span>
                  {localeInfo?.direction === 'rtl' && (
                    <span className="text-[10px] px-1 py-0.5 bg-muted rounded text-muted-foreground">
                      RTL
                    </span>
                  )}
                </span>
                {isSelected && <Check className="h-4 w-4" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
